import {
    YAHOO_ATTRIBUTION_TEXT,
    providerFromPathname,
    providerRequiresAttribution,
} from "./providerPolicy";
import { probabilityHeatStyle } from "./simulationHeatmap";

const COLORS = {
    ink: "#1f3829",
    muted: "#5f665f",
    paper: "#ffffff",
    header: "#1f3829",
    row: "#f8faf7",
    border: "#dce4d6",
    playoff: "#e0a106",
    bye: "#52b788",
};

const xml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const truncate = (value, maxLength = 28) => {
    const text = String(value ?? "");
    return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
};

export const ordinalSuffix = (number) => {
    const suffixes = ["th", "st", "nd", "rd"];
    const value = number % 100;
    return `${number}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

export const formatPercent = (value) => {
    const numericValue = Number(value) || 0;
    const percentage = numericValue * 100;
    return `${percentage.toFixed(numericValue < 0.1 ? 1 : 0)}%`;
};

const getByeCount = (playoffTeams) => {
    const spots = Math.max(0, Number(playoffTeams) || 0);
    return spots < 2 ? 0 : 2 ** Math.ceil(Math.log2(spots)) - spots;
};

const currentAttribution = () => {
    if (typeof window === "undefined") return "";
    const provider = providerFromPathname(window.location?.pathname || "");
    return providerRequiresAttribution(provider) ? YAHOO_ATTRIBUTION_TEXT : "";
};

const withAttribution = (note) => {
    const attribution = currentAttribution();
    if (!attribution) return note;
    return note ? `${note} · ${attribution}` : attribution;
};

const playoffFooter = (playoffTeams, note) => {
    const spots = Math.max(0, Number(playoffTeams) || 0);
    const byes = getByeCount(spots);
    return {
        summary: spots
            ? `${spots} team${spots === 1 ? "" : "s"} make the playoffs${byes ? ` · ${byes} bye${byes === 1 ? "" : "s"}` : ""}`
            : "Simulation results",
        note: withAttribution(note),
    };
};

const playoffMarker = (index, playoffTeams, showByeMarkers = true) => {
    const spots = Math.max(0, Number(playoffTeams) || 0);
    const byes = getByeCount(spots);
    if (showByeMarkers && index < byes) return COLORS.bye;
    if (index < spots) return COLORS.playoff;
    return "transparent";
};

const metricRange = (rows, key) => {
    const values = (rows || [])
        .map((row) => Number(row[key]))
        .filter(Number.isFinite);

    return values.length
        ? { min: Math.min(...values), max: Math.max(...values) }
        : null;
};

const normalizeMetric = (value, range) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || !range) return 0;
    if (range.max === range.min) return 0.5;

    return (numericValue - range.min) / (range.max - range.min);
};

/**
 * A small, data-only table image contract. It intentionally has no dependency
 * on a rendered table, its viewport, or its scroll position. The same contract
 * can be mapped to Pillow by a future scheduled-job renderer.
 */
export const buildPlayoffTableSvg = ({
    title,
    kicker = "PLAYOFF SIMULATION",
    columns,
    rows,
    playoffTeams,
    footer,
    showByeMarkers = true,
}) => {
    const padding = 28;
    const headingHeight = 94;
    const headerHeight = 40;
    const rowHeight = 38;
    const normalizedColumns = columns || [];
    const normalizedRows = rows || [];
    const tableWidth = normalizedColumns.reduce((sum, column) => sum + column.width, 0);
    const width = tableWidth + padding * 2;
    const footerHeight = footer?.summary || footer?.note ? 72 : 28;
    const height = headingHeight + headerHeight + normalizedRows.length * rowHeight + footerHeight;
    const tableX = padding;
    const tableY = headingHeight;

    let dividerX = tableX;
    const columnDividers = normalizedColumns.map((column) => {
        const x = dividerX;
        dividerX += column.width;
        return column.dividerBefore
            ? `<line x1="${x}" y1="${tableY}" x2="${x}" y2="${tableY + headerHeight + normalizedRows.length * rowHeight}" class="column-divider" />`
            : "";
    }).join("");

    let columnX = tableX;
    const headerCells = normalizedColumns.map((column) => {
        const x = columnX;
        columnX += column.width;
        const align = column.align || "middle";
        const textX = align === "start" ? x + 12 : x + column.width / 2;
        return `<text x="${textX}" y="${tableY + 25}" text-anchor="${align === "start" ? "start" : "middle"}" class="header">${xml(String(column.label).toUpperCase())}</text>`;
    }).join("");

    const bodyRows = normalizedRows.map((row, rowIndex) => {
        const y = tableY + headerHeight + rowIndex * rowHeight;
        const stripe = rowIndex % 2 === 0 ? COLORS.paper : COLORS.row;
        const marker = row.markedForPlayoffs
            ? playoffMarker(rowIndex, playoffTeams, showByeMarkers)
            : "transparent";
        let cellX = tableX;
        const cells = normalizedColumns.map((column, columnIndex) => {
            const x = cellX;
            cellX += column.width;
            const align = column.align || "middle";
            const textX = align === "start" ? x + 12 : x + column.width / 2;
            const cell = row.cells[columnIndex] || {};
            const className = `${column.strong || cell.strong ? "cell strong" : "cell"} ${align === "start" && columnIndex === 0 ? "team" : ""}`;
            const heatStyle = cell.heatValue === undefined || cell.heatValue === null
                ? null
                : probabilityHeatStyle(cell.heatValue);
            const heatRect = heatStyle
                ? `<rect x="${x}" y="${y}" width="${column.width}" height="${rowHeight}" fill="${heatStyle.backgroundColor}" stroke="${heatStyle.borderColor}" stroke-width="1" class="heat-cell" />`
                : "";
            return `${heatRect}<text x="${textX}" y="${y + 24}" text-anchor="${align === "start" ? "start" : "middle"}" class="${className}">${xml(truncate(cell.value, column.maxLength))}</text>`;
        }).join("");
        return `
            <rect x="${tableX}" y="${y}" width="${tableWidth}" height="${rowHeight}" fill="${stripe}" />
            <rect x="${tableX}" y="${y}" width="4" height="${rowHeight}" fill="${marker}" />
            <line x1="${tableX}" y1="${y + rowHeight}" x2="${tableX + tableWidth}" y2="${y + rowHeight}" class="divider" />
            ${cells}`;
    }).join("");

    return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${xml(title)}">
            <style>
                .kicker { fill: #8a5a00; font: 800 11px Arial, sans-serif; letter-spacing: 1.5px; }
                .title { fill: ${COLORS.ink}; font: 700 27px Georgia, serif; }
                .header { fill: #ffffff; font: 800 10px Arial, sans-serif; letter-spacing: .7px; }
                .team { fill: ${COLORS.ink}; font: 700 13px Arial, sans-serif; }
                .cell { fill: ${COLORS.ink}; font: 600 13px Arial, sans-serif; }
                .strong { font-weight: 800; }
                .divider { stroke: ${COLORS.border}; stroke-width: 1; }
                .column-divider { stroke: #000000; stroke-width: 1; }
                .note { fill: ${COLORS.muted}; font: italic 11px Arial, sans-serif; }
                .summary { fill: ${COLORS.muted}; font: 700 11px Arial, sans-serif; }
            </style>
            <rect width="100%" height="100%" fill="${COLORS.paper}" />
            <text x="${padding}" y="29" class="kicker">${xml(kicker)}</text>
            <text x="${padding}" y="63" class="title">${xml(title)}</text>
            <rect x="${tableX}" y="${tableY}" width="${tableWidth}" height="${headerHeight}" rx="5" fill="${COLORS.header}" />
            ${headerCells}
            ${bodyRows}
            ${columnDividers}
            ${footer?.summary ? `<text x="${padding}" y="${height - 38}" class="summary">${xml(footer.summary)}</text>` : ""}
            ${footer?.note ? `<text x="${padding}" y="${height - 18}" class="note">${xml(footer.note)}</text>` : ""}
        </svg>`;
};

export const playoffTableImageBlob = async (options) => {
    const svg = buildPlayoffTableSvg(options);
    const svgUrl = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
    try {
        const image = await new Promise((resolve, reject) => {
            const nextImage = new Image();
            nextImage.onload = () => resolve(nextImage);
            nextImage.onerror = reject;
            nextImage.src = svgUrl;
        });
        const scale = 2;
        const canvas = document.createElement("canvas");
        canvas.width = image.width * scale;
        canvas.height = image.height * scale;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Could not create a canvas for the table image.");
        context.scale(scale, scale);
        context.drawImage(image, 0, 0);
        return await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not create the table image.")), "image/png");
        });
    } finally {
        URL.revokeObjectURL(svgUrl);
    }
};

const teamColumns = [
    { label: "Team", width: 186, align: "start", maxLength: 25 },
    { label: "Owner", width: 164, align: "start", maxLength: 23 },
];

const simulationNote = "Simulation results only. 0% or 100% does not necessarily mean eliminated or clinched.";

export const rankDistributionImageOptions = ({ data, numColumns, playoffTeams, selectedWeek }) => ({
    title: "Final Position Distribution",
    kicker: `PLAYOFF SIMULATION · PRIOR TO WEEK ${selectedWeek}`,
    columns: [
        ...teamColumns,
        ...Array.from({ length: Math.max(0, Number(numColumns) || 0) }, (_, index) => ({ label: ordinalSuffix(index + 1), width: 76 })),
        { label: "Playoff odds", width: 108, strong: true },
    ],
    rows: (data || []).map((team) => ({
        markedForPlayoffs: true,
        cells: [
            { value: team.team },
            { value: team.owner },
            ...(team.position_odds || []).slice(0, numColumns).map((value) => ({
                value: formatPercent(value),
                heatValue: value,
            })),
            {
                value: formatPercent(team.playoff_odds),
                strong: true,
                heatValue: team.playoff_odds,
            },
        ],
    })),
    playoffTeams,
    showByeMarkers: false,
    footer: playoffFooter(playoffTeams, simulationNote),
});

export const playoffOddsImageOptions = ({ data, playoffTeams, selectedWeek, cached }) => ({
    title: "Playoff Odds",
    kicker: `PLAYOFF SIMULATION · PRIOR TO WEEK ${selectedWeek}${cached ? " · CACHED" : ""}`,
    columns: [
        ...teamColumns,
        { label: "Projected wins", width: 112 },
        { label: "Projected losses", width: 118 },
        { label: "Projected ties", width: 100 },
        { label: "Projected points for", width: 152 },
        { label: "Playoff odds", width: 108, strong: true },
    ],
    rows: (data || []).map((team) => ({
        markedForPlayoffs: true,
        cells: [
            { value: team.team }, { value: team.owner },
            { value: Number(team.projected_wins).toFixed(1) },
            { value: Number(team.projected_losses).toFixed(1) },
            { value: Number(team.projected_ties).toFixed(1) },
            { value: Number(team.projected_points_for).toFixed(2) },
            {
                value: formatPercent(team.playoff_odds),
                strong: true,
                heatValue: team.playoff_odds,
            },
        ],
    })),
    playoffTeams,
    showByeMarkers: false,
    footer: playoffFooter(playoffTeams, simulationNote),
});

export const seedingOutcomesImageOptions = ({ data, playoffTeams, selectedWeek }) => ({
    title: "Seeding Outcomes",
    kicker: `PLAYOFF SIMULATION · PRIOR TO WEEK ${selectedWeek}`,
    columns: [
        ...teamColumns,
        { label: "First in league", width: 122 },
        { label: "First in division", width: 132 },
        { label: "Make playoffs", width: 118, strong: true },
        { label: "Last in division", width: 132 },
        { label: "Last in league", width: 122 },
    ],
    rows: (data || []).map((team) => ({
        markedForPlayoffs: true,
        cells: [
            { value: team.team }, { value: team.owner },
            { value: formatPercent(team.first_in_league), heatValue: team.first_in_league },
            { value: formatPercent(team.first_in_division), heatValue: team.first_in_division },
            { value: formatPercent(team.make_playoffs), strong: true, heatValue: team.make_playoffs },
            { value: formatPercent(team.last_in_division), heatValue: team.last_in_division },
            { value: formatPercent(team.last_in_league), heatValue: team.last_in_league },
        ],
    })),
    playoffTeams,
    showByeMarkers: false,
    footer: playoffFooter(playoffTeams, simulationNote),
});

export const remainingScheduleImageOptions = ({ data, title }) => {
    const rows = data || [];
    const ranges = {
        opp_points_for: metricRange(rows, "opp_points_for"),
        opp_win_pct: metricRange(rows, "opp_win_pct"),
        opp_power_rank: metricRange(rows, "opp_power_rank"),
        overall_difficulty: metricRange(rows, "overall_difficulty"),
    };
    const heatValue = (team, key) => normalizeMetric(team[key], ranges[key]);

    return {
        title,
        kicker: "PLAYOFF SIMULATION",
        columns: [
            ...teamColumns,
            { label: "Opponent points for", width: 148 },
            { label: "Opponent win %", width: 126 },
            { label: "Opponent power rank", width: 154 },
            { label: "Overall difficulty", width: 134, strong: true, dividerBefore: true },
        ],
        rows: rows.map((team) => ({
            cells: [
                { value: team.team }, { value: team.owner },
                {
                    value: Number(team.opp_points_for).toFixed(2),
                    heatValue: heatValue(team, "opp_points_for"),
                },
                {
                    value: Number(team.opp_win_pct).toFixed(3),
                    heatValue: heatValue(team, "opp_win_pct"),
                },
                {
                    value: Number(team.opp_power_rank).toFixed(1),
                    heatValue: heatValue(team, "opp_power_rank"),
                },
                {
                    value: Number(team.overall_difficulty).toFixed(1),
                    strong: true,
                    heatValue: heatValue(team, "overall_difficulty"),
                },
            ],
        })),
    };
};
